import {
  Box,
  Button,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  useToast,
} from "@chakra-ui/react";
import { ArrowUpRight, CaretDown, X } from "@phosphor-icons/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { ResponsiveBar } from "@nivo/bar";
import { useTooltip } from "@nivo/tooltip";
import { useGetTotalEmissions } from "../../../../hooks/query/emissions-insight/emissions-insights-query";
import { useGetOrganizationStructure } from "../../../../hooks/query";
import { useGetCompanyDimensionReports, useGetCompanyTotalEmissionsReports } from "../../../../hooks/query/reports";
import { YearStartEndDropdown } from "../supplier-components/SupplierTrends";
import LocalSpinner from "../../../../components/LocalSpinner";
import { scopeThreeCategories } from "../../../Reports/CompanyEmissionsDashboard/components/helpers";
import { useTranslation } from "react-i18next";

const SHOW_OPTIONS = [
  { label: "Scope 1", value: "scope1" },
  { label: "Scope 2", value: "scope2" },
  { label: "Scope 3", value: "scope3" },
];

// Scope 1 sub-categories shown as the top-level bars. Clicking one drills
// into a focused waterfall of its consumption types.
const SCOPE1_CATEGORIES = [
  { key: "stationary", label: "Stationary" },
  { key: "mobile", label: "Mobile" },
  { key: "refrigerant", label: "Refrigerant" },
];

const BASIS_OPTIONS = [
  { label: "Location based", value: "location" },
  { label: "Market based", value: "market" },
];

// Scope 2 v2 purchased-electricity categories (backend ReportElectricityV2Item
// category codes + the ONSITE summary row). Same labels as ScopeTwoDetail.
const SCOPE2_V2_LABELS = {
  STANDARD_GRID: "Standard Grid",
  SUPPLIER_CONTRACTED: "Supplier Contracted",
  CERTIFIED_RENEWABLE: "Certified Renewable",
  DIRECT_PRIVATE_WIRE: "Direct / Private Wire",
  ONSITE: "On-site generation",
};

// Scope 2 v1 electricity types (ElectricityType enum names on
// scopeOneAndTwoDetail.electricity rows). The location total excludes the
// market-based rows and vice versa, so each basis lists its own type set.
const SCOPE2_V1_LABELS = {
  ELECTRICITY_LOCATION_BASED: "Location based",
  ELECTRICITY_MARKET_BASED: "Market based",
  RENEWABLE_ENERGY_PROCUREMENT: "Renewable energy procurement",
  ELECTRICITY_ONSITE_GENERATION: "On-site production",
};
const SCOPE2_V1_EXCLUDED_BY_BASIS = {
  location: "ELECTRICITY_MARKET_BASED",
  market: "ELECTRICITY_LOCATION_BASED",
};

const DIMENSION_LEVEL_KEYS = ["dimensionOne", "dimensionTwo", "dimensionThree", "dimensionFour", "dimensionFive"];

// Parse the FootPrint dimensionParams string ("dimensionOne=1,2&dimensionTwo=3")
// back into per-level request params for the by-dimension reports endpoint.
const parseDimensionParams = (str) =>
  Object.fromEntries(
    (str || "")
      .split("&")
      .filter(Boolean)
      .map((pair) => pair.split("="))
      .filter(([k, v]) => DIMENSION_LEVEL_KEYS.includes(k) && v)
  );

// Sum a year's per-dimension-item electricityV2 rows into per-category
// location/market totals ({ STANDARD_GRID: { location, market }, ... }).
const aggregateV2Categories = (reportContent) => {
  const byCategory = {};
  (reportContent || []).forEach((row) => {
    (row.electricityV2 || []).forEach((c) => {
      const k = c.category;
      if (!byCategory[k]) byCategory[k] = { location: 0, market: 0 };
      byCategory[k].location += Number(c.locationCo2Emissions) || 0;
      byCategory[k].market += Number(c.marketCo2Emissions) || 0;
    });
  });
  return byCategory;
};

const WATERFALL_COLORS = {
  start: "#CBD5E0",
  end: "#718096",
  decrease: "#C5E86C",
  increase: "#FC8181",
};

const sumEmissions = (items) =>
  (items || []).reduce((sum, item) => sum + (item.emissions || 0), 0);

const getScopeTotal = (data, scopeValue, basis) => {
  if (!data) return 0;
  switch (scopeValue) {
    case "scope1":
      return data.scope1Total || 0;
    case "scope2":
      return (basis === "market" ? data.scope2MarketTotal : data.scope2LocationTotal) || 0;
    case "scope3":
      return data.scope3Total || 0;
    default:
      return 0;
  }
};

const buildWaterfallBars = (fromYear, toYear, fromTotal, toTotal, changes) => {
  const bars = [];
  let running = fromTotal;
  // Percentages are relative to the starting-year total so every bar shares
  // the same base (a zero start year means no meaningful %).
  const pctOf = (delta) =>
    fromTotal !== 0 ? (delta / Math.abs(fromTotal)) * 100 : null;

  // When the year totals dwarf the individual changes (e.g. ±2% moves on a
  // 400k total) an axis from zero squashes every change bar into a sliver.
  // Zoom: start the axis just below the lowest running level so the walk
  // itself fills the chart. Totals keep their real display values.
  let runningLevel = fromTotal;
  let minLevel = Math.min(fromTotal, toTotal);
  let maxLevel = Math.max(fromTotal, toTotal);
  for (const item of changes) {
    runningLevel += item.change;
    minLevel = Math.min(minLevel, runningLevel);
    maxLevel = Math.max(maxLevel, runningLevel);
  }
  const span = maxLevel - minLevel;
  // Round the floor down to a clean step so the re-based axis tick labels stay
  // round numbers instead of inheriting the raw floor's decimals.
  const floorStep = Math.max(10 ** Math.floor(Math.log10(Math.max(span, 10))), 10);
  const floor =
    minLevel > 0 && span > 0 && span / maxLevel < 0.35
      ? Math.max(0, Math.floor((minLevel - span * 0.15) / floorStep) * floorStep)
      : 0;

  // Bars are plotted in offset space (real value minus floor); the axis tick
  // labels add the floor back so gridline values stay truthful.
  bars.push({
    label: String(fromYear),
    base: 0,
    value: Math.max(fromTotal - floor, 0),
    type: "start",
    displayValue: fromTotal,
  });

  for (const item of changes) {
    if (item.change === 0) continue;
    if (item.change < 0) {
      bars.push({
        label: item.label,
        fullLabel: item.fullLabel,
        base: running + item.change - floor,
        value: Math.abs(item.change),
        type: "decrease",
        displayValue: item.change,
        displayPct: pctOf(item.change),
        drillKey: item.drillKey,
      });
    } else {
      bars.push({
        label: item.label,
        fullLabel: item.fullLabel,
        base: running - floor,
        value: item.change,
        type: "increase",
        displayValue: item.change,
        displayPct: pctOf(item.change),
        drillKey: item.drillKey,
      });
    }
    running += item.change;
  }

  bars.push({
    label: String(toYear),
    base: 0,
    value: Math.max(toTotal - floor, 0),
    type: "end",
    displayValue: toTotal,
    displayPct: pctOf(toTotal - fromTotal),
  });

  return { bars, floor };
};

const formatPct = (pct) => {
  if (pct === null || pct === undefined || !isFinite(pct)) return null;
  const rounded = Math.abs(pct) < 10 ? Math.round(pct * 10) / 10 : Math.round(pct);
  return `${rounded > 0 ? "+" : ""}${rounded}%`;
};

// Hover tooltip matching the shared Insights chart tooltip style
// (chartTheme.makeBarTooltip) — bold title row with a colour swatch, value
// line underneath. Shows the full category description for abbreviated
// Scope 3 "Cat N" bars. Base (spacer) bars render nothing.
const WaterfallTooltip = ({ id, color, data }) => {
  if (id === "base") return null;
  const isTotal = data.type === "start" || data.type === "end";
  const value = data.displayValue;
  const prefix = !isTotal && value > 0 ? "+" : "";
  const pctLabel = !isTotal ? formatPct(data.displayPct) : null;
  return (
    <div
      style={{
        background: "white",
        padding: "8px 12px",
        border: "1px solid #ccc",
        borderRadius: 4,
        boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
        fontSize: 13,
        color: "#4A5568",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
        <span style={{ width: 10, height: 10, background: color, borderRadius: 2, display: "inline-block" }} />
        <span style={{ fontWeight: 700, color: "#2D3748" }}>{data.fullLabel || data.label}</span>
      </div>
      <div>
        {prefix}
        {Number(value).toLocaleString("en-GB", { maximumFractionDigits: 2 })} tCO2e
        {pctLabel && <span style={{ color: "#A0AEC0", marginLeft: 8 }}>· {pctLabel}</span>}
      </div>
    </div>
  );
};

const WaterfallLabels = ({ bars }) => (
  <g>
    {bars
      .filter((bar) => bar.data.id === "value" && bar.data.value > 0)
      .map((bar) => {
        const d = bar.data.data;
        const isTotal = d.type === "start" || d.type === "end";
        const pctLabel = formatPct(d.displayPct);

        if (isTotal) {
          return (
            <g key={bar.key}>
              <text
                x={bar.x + bar.width / 2}
                y={d.type === "end" && pctLabel ? bar.y - 24 : bar.y - 10}
                textAnchor="middle"
                fontSize={12}
                fontWeight="bold"
                fill="#2D3748"
              >
                {d.displayValue?.toLocaleString("en-GB")}
              </text>
              {d.type === "end" && pctLabel && (
                <text
                  x={bar.x + bar.width / 2}
                  y={bar.y - 10}
                  textAnchor="middle"
                  fontSize={11}
                  fontWeight="600"
                  fill={d.displayPct > 0 ? "#9B2C2C" : "#276749"}
                >
                  {pctLabel} vs start
                </text>
              )}
            </g>
          );
        }

        const prefix = d.displayValue > 0 ? "+" : "";
        const valueLine = `${prefix}${Math.round(d.displayValue).toLocaleString("en-GB")}`;
        const fill = d.type === "increase" ? "#9B2C2C" : "#276749";
        const centerX = bar.x + bar.width / 2;

        // Value and % stack at a fixed offset above every change bar; the
        // (short) category label sits directly under the bar.
        return (
          <g key={bar.key}>
            <text
              x={centerX}
              y={bar.y - 21}
              textAnchor="middle"
              fontSize={11}
              fontWeight="600"
              fill={fill}
            >
              {valueLine}
            </text>
            {pctLabel && (
              <text
                x={centerX}
                y={bar.y - 8}
                textAnchor="middle"
                fontSize={10}
                fontWeight="600"
                fill={fill}
              >
                ({pctLabel})
              </text>
            )}
            <text
              x={centerX}
              y={bar.y + bar.height + 14}
              textAnchor="middle"
              fontSize={10}
              fill="#718096"
            >
              {d.label}
            </text>
          </g>
        );
      })}
  </g>
);


const WaterfallConnectors = ({ bars }) => {
  const valueBars = bars.filter(
    (bar) => bar.data.id === "value" && bar.data.value > 0
  );

  return (
    <g>
      {valueBars.slice(0, -1).map((bar, i) => {
        const nextBar = valueBars[i + 1];
        if (!nextBar) return null;
        const d = bar.data.data;

        const connectorY =
          d.type === "decrease" ? bar.y + bar.height : bar.y;

        return (
          <line
            key={`connector-${i}`}
            x1={bar.x + bar.width}
            y1={connectorY}
            x2={nextBar.x}
            y2={connectorY}
            stroke="#A0AEC0"
            strokeWidth={1}
            strokeDasharray="4,3"
          />
        );
      })}
    </g>
  );
};

const FilterDropdown = ({ options, selected, onSelect, prefix }) => (
  <Menu>
    <MenuButton
      as={Button}
      size="sm"
      borderRadius="full"
      border="1px solid"
      borderColor="gray.300"
      bg="transparent"
      color="gray.600"
      fontWeight="normal"
      rightIcon={<CaretDown size={14} />}
      _hover={{ bg: "gray.50", borderColor: "gray.400" }}
    >
      {prefix}: {selected.label}
    </MenuButton>
    <MenuList
      borderRadius="lg"
      border="1px solid"
      borderColor="gray.200"
      boxShadow="md"
      py={1}
    >
      {options.map((opt) => (
        <MenuItem
          key={opt.value}
          onClick={() => onSelect(opt)}
          fontWeight={selected.value === opt.value ? "bold" : "normal"}
        >
          {opt.label}
        </MenuItem>
      ))}
    </MenuList>
  </Menu>
);

const WaterfallChart = ({ dimensionParams, selectedYear, quarter }) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "dataEntryPlanning.modal.categories",
  });
  const toast = useToast();
  const defaultTo = selectedYear || new Date().getFullYear();
  const [startYear, setStartYear] = useState(defaultTo - 1);
  const [endYear, setEndYear] = useState(defaultTo);
  // Track whether the user has manually adjusted the waterfall year range.
  // If not, keep the waterfall in sync with the page-level year selection.
  const userOverrodeYears = useRef(false);

  useEffect(() => {
    if (userOverrodeYears.current) return;
    if (!selectedYear) return;
    setEndYear(selectedYear);
    setStartYear(selectedYear - 1);
  }, [selectedYear]);

  const handleSetStartYear = (y) => { userOverrodeYears.current = true; setStartYear(y); };
  const handleSetEndYear = (y) => { userOverrodeYears.current = true; setEndYear(y); };
  const [showScope, setShowScope] = useState(SHOW_OPTIONS[0]);
  const [basisOption, setBasisOption] = useState(BASIS_OPTIONS[0]);
  // Scope 1 drill-down: null = sub-category view, otherwise the key of the
  // sub-category whose consumption types are shown ("stationary" | ...).
  const [drillCategory, setDrillCategory] = useState(null);

  const handleSelectScope = (opt) => {
    setDrillCategory(null);
    setShowScope(opt);
  };

  const { data: fromData, isFetching: isFetchingFrom } = useGetTotalEmissions(
    startYear,
    dimensionParams,
    {
      onError: (error) => {
        // 404 = no report for that year; the waterfall just shows the years
        // that do have data. Only toast genuine errors.
        if (error?.response?.status === 404) return;
        toast({
          title: "Error fetching emissions data",
          description: error?.response?.data?.parameters?.message || error.message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      },
      retry: false,
    },
    quarter
  );

  const { data: toData, isFetching: isFetchingTo } = useGetTotalEmissions(
    endYear,
    dimensionParams,
    {
      onError: (error) => {
        // 404 = no report for that year; the waterfall just shows the years
        // that do have data. Only toast genuine errors.
        if (error?.response?.status === 404) return;
        toast({
          title: "Error fetching emissions data",
          description: error?.response?.data?.parameters?.message || error.message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      },
      retry: false,
    },
    quarter
  );

  // Scope 2 v2 category breakdown (Standard Grid, Supplier Contracted, ...) is
  // not on the /insights/total payload — it lives on the by-dimension reports
  // rows (electricityV2, same source ScopeTwoDetail aggregates). Fetch the
  // rows at the next-level dimension for both years when Scope 2 is shown.
  const { data: organizationStructure } = useGetOrganizationStructure();
  const dimensionFilter = useMemo(() => parseDimensionParams(dimensionParams), [dimensionParams]);
  const v2DimensionId = useMemo(() => {
    const configuredKeys = DIMENSION_LEVEL_KEYS.filter(
      (k) => organizationStructure?.dimensions?.[k]?.id && organizationStructure?.dimensions?.[k]?.name
    );
    if (configuredKeys.length === 0) return null;
    const selectedKeys = DIMENSION_LEVEL_KEYS.filter((k) => dimensionFilter[k]);
    const currentKey = selectedKeys.length > 0 ? selectedKeys[selectedKeys.length - 1] : null;
    const nextKey = configuredKeys[currentKey ? configuredKeys.indexOf(currentKey) + 1 : 0];
    return organizationStructure?.dimensions?.[nextKey]?.id || null;
  }, [organizationStructure, dimensionFilter]);

  const isScopeTwo = showScope.value === "scope2";
  const isScopeThree = showScope.value === "scope3";
  const reportQuarter = quarter || "ALL";

  // Scope 3 per-category totals (categoryOne..FifteenTotalCo2Emissions) are on
  // the company total-emissions report, not the /insights/total payload — same
  // source SummaryCards and ScopeThreeDetail read.
  const { data: fromScope3, isFetching: isFetchingFromScope3 } = useGetCompanyTotalEmissionsReports(
    { year: startYear, quarter: reportQuarter },
    { enabled: isScopeThree && !!startYear }
  );
  const { data: toScope3, isFetching: isFetchingToScope3 } = useGetCompanyTotalEmissionsReports(
    { year: endYear, quarter: reportQuarter },
    { enabled: isScopeThree && !!endYear }
  );
  const { data: fromV2Report, isFetching: isFetchingFromV2 } = useGetCompanyDimensionReports(
    v2DimensionId,
    { year: startYear, quarter: reportQuarter, page: 0, size: 200, ...dimensionFilter },
    { enabled: isScopeTwo && !!v2DimensionId && !!startYear }
  );
  const { data: toV2Report, isFetching: isFetchingToV2 } = useGetCompanyDimensionReports(
    v2DimensionId,
    { year: endYear, quarter: reportQuarter, page: 0, size: 200, ...dimensionFilter },
    { enabled: isScopeTwo && !!v2DimensionId && !!endYear }
  );

  const isFetching =
    isFetchingFrom ||
    isFetchingTo ||
    (isScopeTwo && (isFetchingFromV2 || isFetchingToV2)) ||
    (isScopeThree && (isFetchingFromScope3 || isFetchingToScope3));

  const { bars: waterfallData, floor: axisFloor } = useMemo(() => {
    if (!fromData || !toData) return { bars: [], floor: 0 };

    const getConsumptionTypeChanges = (categories) => {
      const fromMap = {};
      const toMap = {};
      categories.forEach((cat) => {
        (fromData.scopeOneAndTwoDetail?.[cat] || []).forEach((item) => {
          fromMap[item.consumptionType] =
            (fromMap[item.consumptionType] || 0) + (item.emissions || 0);
        });
        (toData.scopeOneAndTwoDetail?.[cat] || []).forEach((item) => {
          toMap[item.consumptionType] =
            (toMap[item.consumptionType] || 0) + (item.emissions || 0);
        });
      });
      const allTypes = new Set([
        ...Object.keys(fromMap),
        ...Object.keys(toMap),
      ]);
      return Array.from(allTypes)
        .map((type) => ({
          label: type,
          change: (toMap[type] || 0) - (fromMap[type] || 0),
        }))
        .filter((c) => c.change !== 0)
        .sort((a, b) => a.change - b.change);
    };

    const getSubcategoryChanges = () => {
      const scopeVal = showScope.value;

      if (scopeVal === "scope3") {
        return scopeThreeCategories
          .map((cat, i) => {
            const fromVal =
              Number(fromScope3?.[`category${cat.numberKey}TotalCo2Emissions`]) || 0;
            const toVal =
              Number(toScope3?.[`category${cat.numberKey}TotalCo2Emissions`]) || 0;
            // Short axis label; the full category name shows on bar hover.
            return {
              label: `Cat ${i + 1}`,
              fullLabel: `Cat ${i + 1} — ${t(cat.translationKey)}`,
              change: toVal - fromVal,
            };
          })
          .filter((c) => c.change !== 0)
          .sort((a, b) => a.change - b.change);
      }

      if (scopeVal === "scope2") {
        const basis = basisOption.value;

        // Scope 2 v2 orgs: per-category rows (Standard Grid, Supplier
        // Contracted, Certified Renewable, Direct / Private Wire, On-site)
        // carry both location- and market-based tCO2e.
        const fromV2 = aggregateV2Categories(fromV2Report?.content);
        const toV2 = aggregateV2Categories(toV2Report?.content);
        const hasV2 = Object.keys(fromV2).length > 0 || Object.keys(toV2).length > 0;
        if (hasV2) {
          return Object.keys(SCOPE2_V2_LABELS)
            .map((cat) => ({
              label: SCOPE2_V2_LABELS[cat],
              change: (toV2[cat]?.[basis] || 0) - (fromV2[cat]?.[basis] || 0),
            }))
            .filter((c) => c.change !== 0)
            .sort((a, b) => a.change - b.change);
        }

        // Scope 2 v1 orgs: electricity detail rows are typed by the old
        // ElectricityType enum. Location and market totals each exclude the
        // other basis's rows (mirrors the backend total calc), leaving
        // location/market based, renewable procurement and on-site production.
        const excluded = SCOPE2_V1_EXCLUDED_BY_BASIS[basis];
        const typeMap = (d) => {
          const map = {};
          (d.scopeOneAndTwoDetail?.electricity || []).forEach((item) => {
            if (item.consumptionType === excluded) return;
            map[item.consumptionType] =
              (map[item.consumptionType] || 0) + (item.emissions || 0);
          });
          return map;
        };
        const fromMap = typeMap(fromData);
        const toMap = typeMap(toData);
        const allTypes = new Set([...Object.keys(fromMap), ...Object.keys(toMap)]);
        return Array.from(allTypes)
          .map((type) => ({
            label: SCOPE2_V1_LABELS[type] || type,
            change: (toMap[type] || 0) - (fromMap[type] || 0),
          }))
          .filter((c) => c.change !== 0)
          .sort((a, b) => a.change - b.change);
      }

      // Scope 1 drilled into a sub-category: its consumption-type changes.
      if (drillCategory) {
        return getConsumptionTypeChanges([drillCategory]);
      }

      // Scope 1 top level: one bar per sub-category, clickable to drill.
      return SCOPE1_CATEGORIES
        .map((cat) => ({
          label: cat.label,
          drillKey: cat.key,
          change:
            sumEmissions(toData.scopeOneAndTwoDetail?.[cat.key]) -
            sumEmissions(fromData.scopeOneAndTwoDetail?.[cat.key]),
        }))
        .filter((c) => c.change !== 0);
    };

    const isDrilled = showScope.value === "scope1" && drillCategory;
    // Drilled view starts and ends on the sub-category's own totals, not the
    // full Scope 1 total.
    const fromTotal = isDrilled
      ? sumEmissions(fromData.scopeOneAndTwoDetail?.[drillCategory])
      : getScopeTotal(fromData, showScope.value, basisOption.value);
    const toTotal = isDrilled
      ? sumEmissions(toData.scopeOneAndTwoDetail?.[drillCategory])
      : getScopeTotal(toData, showScope.value, basisOption.value);
    const changes = getSubcategoryChanges();

    // Add "Other" bar to account for emissions not captured in the detail breakdown
    const totalDiff = toTotal - fromTotal;
    const sumOfChanges = changes.reduce((sum, c) => sum + c.change, 0);
    const unaccounted = totalDiff - sumOfChanges;
    if (Math.abs(unaccounted) > 0.5) {
      changes.push({ label: "Other", change: unaccounted });
    }

    return buildWaterfallBars(startYear, endYear, fromTotal, toTotal, changes);
  }, [fromData, toData, fromV2Report, toV2Report, fromScope3, toScope3, showScope, drillCategory, basisOption, startYear, endYear, t]);

  const hasData = waterfallData.length > 2;

  // Transparent overlay making Scope 1 sub-category bars clickable to drill
  // into their consumption types. Defined inside the component so it can
  // close over the drill state (nivo layers receive no extra props). Hover
  // shows the styled chart tooltip (via nivo's tooltip context, same card as
  // WaterfallTooltip) instead of a native browser title.
  const canDrill = showScope.value === "scope1" && !drillCategory;
  const DrillClickLayer = ({ bars, innerHeight }) => {
    const { showTooltipFromEvent, hideTooltip } = useTooltip();
    if (!canDrill) return null;

    const drillTooltip = (bar) => (
      <div
        style={{
          background: "white",
          padding: "8px 12px",
          border: "1px solid #ccc",
          borderRadius: 4,
          boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
          fontSize: 13,
          color: "#4A5568",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
          <span
            style={{
              width: 10,
              height: 10,
              background: bar.color,
              borderRadius: 2,
              display: "inline-block",
            }}
          />
          <span style={{ fontWeight: 700, color: "#2D3748" }}>{bar.data.data.label}</span>
        </div>
        <div>Click to drill into its consumption types ↗</div>
      </div>
    );

    return (
      <g>
        {bars
          .filter((bar) => bar.data.id === "value" && bar.data.data.drillKey)
          .map((bar) => {
            // Hit area spans the whole column (top of plot down through the
            // labels below the axis), padded sideways into the bar gutter, so
            // small bars are still easy to click.
            const padX = bar.width * 0.2;
            const centerX = bar.x + bar.width / 2;
            return (
              <g key={`drill-${bar.key}`}>
                <ArrowUpRight
                  x={centerX - 6}
                  y={bar.y + bar.height + 20}
                  size={12}
                  weight="bold"
                  color="#718096"
                  style={{ pointerEvents: "none" }}
                />
                <rect
                  x={bar.x - padX}
                  y={0}
                  width={bar.width + padX * 2}
                  height={innerHeight + 40}
                  fill="transparent"
                  style={{ cursor: "pointer" }}
                  onClick={() => setDrillCategory(bar.data.data.drillKey)}
                  onMouseEnter={(e) => showTooltipFromEvent(drillTooltip(bar), e)}
                  onMouseMove={(e) => showTooltipFromEvent(drillTooltip(bar), e)}
                  onMouseLeave={hideTooltip}
                />
              </g>
            );
          })}
      </g>
    );
  };

  const drilledLabel = SCOPE1_CATEGORIES.find((c) => c.key === drillCategory)?.label;

  const getBarColor = ({ id, data }) => {
    if (id === "base") return "transparent";
    switch (data.type) {
      case "start":
        return WATERFALL_COLORS.start;
      case "end":
        return WATERFALL_COLORS.end;
      case "decrease":
        return WATERFALL_COLORS.decrease;
      case "increase":
        return WATERFALL_COLORS.increase;
      default:
        return WATERFALL_COLORS.start;
    }
  };

  return (
    <Flex direction="column" gap={3}>
      <Flex gap={2} flexWrap="wrap" align="center">
        <YearStartEndDropdown
          setStartYear={handleSetStartYear}
          startYear={startYear}
          setEndYear={handleSetEndYear}
          endYear={endYear}
        />
        <FilterDropdown
          options={SHOW_OPTIONS}
          selected={showScope}
          onSelect={handleSelectScope}
          prefix="Show"
        />
        {showScope.value === "scope1" &&
          (drilledLabel ? (
            <Button
              size="sm"
              borderRadius="full"
              border="1px solid"
              borderColor="gray.300"
              bg="gray.100"
              color="gray.500"
              fontWeight="normal"
              rightIcon={<X size={14} weight="bold" />}
              _hover={{ bg: "gray.200", borderColor: "gray.400" }}
              onClick={() => setDrillCategory(null)}
              aria-label={`Back to sub-categories (currently showing ${drilledLabel})`}
            >
              {drilledLabel}
            </Button>
          ) : (
            <FilterDropdown
              options={SCOPE1_CATEGORIES.map((cat) => ({ value: cat.key, label: cat.label }))}
              selected={{ value: null, label: "All" }}
              onSelect={(opt) => setDrillCategory(opt.value)}
              prefix="Sub category"
            />
          ))}
        {showScope.value === "scope2" && (
          <FilterDropdown
            options={BASIS_OPTIONS}
            selected={basisOption}
            onSelect={setBasisOption}
            prefix="Basis"
          />
        )}
      </Flex>

      <Box>
        <Text fontSize="body.m" fontWeight="600" color="gray.700">
          {drilledLabel ? `Emissions change — ${drilledLabel}` : "Emissions change"}
        </Text>
        <Text fontSize="body.xs" color="gray.500">
          {drilledLabel
            ? `Waterfall of ${drilledLabel} consumption types between ${startYear} and ${endYear}.`
            : `Waterfall of what rose or fell between ${startYear} and ${endYear}.`}{" "}
          Green bars reduce the total, red bars add to it.
          {canDrill &&
            " Click any sub-category bar to drill down into its consumption types."}
        </Text>
      </Box>

      {isFetching && <LocalSpinner />}

      <Box height="420px" width="100%">
        {hasData && !isFetching && (
          <ResponsiveBar
            data={waterfallData}
            keys={["base", "value"]}
            indexBy="label"
            padding={0.3}
            margin={{ top: 48, right: 30, bottom: 60, left: 80 }}
            colors={getBarColor}
            borderRadius={4}
            enableLabel={false}
            enableGridY={true}
            gridYValues={6}
            isInteractive={true}
            tooltip={WaterfallTooltip}
            animate={true}
            layers={[
              "grid",
              "axes",
              "bars",
              WaterfallConnectors,
              WaterfallLabels,
              DrillClickLayer,
            ]}
            theme={{
              axis: {
                ticks: {
                  text: { fontSize: 12, fill: "#718096" },
                },
                legend: {
                  text: { fontSize: 13, fill: "#2D3748", fontWeight: 600 },
                },
              },
            }}
            axisBottom={{
              renderTick: ({ x, y, value }) => {
                const isYear = /^\d{4}$/.test(value);
                if (isYear) {
                  return (
                    <g transform={`translate(${x},${y})`}>
                      <text
                        textAnchor="middle"
                        dominantBaseline="central"
                        y={16}
                        style={{
                          fontSize: 13,
                          fill: "#2D3748",
                          fontWeight: 600,
                        }}
                      >
                        {value}
                      </text>
                    </g>
                  );
                }
                // Category labels render under the bars (WaterfallLabels),
                // not on the axis.
                return <g />;
              },
            }}
            axisLeft={{
              legend: "Total Emissions (tCO2e)",
              legendOffset: -65,
              legendPosition: "middle",
              tickPadding: 6,
              tickValues: 6,
              // Bars are plotted relative to the zoom floor; ticks show the
              // real level so the axis stays truthful.
              format: (v) => (v + axisFloor).toLocaleString("en-GB"),
            }}
          />
        )}
        {!hasData && !isFetching && (
          <Flex align="center" justify="center" h="100%" color="gray.400">
            <Text>No change data available for the selected filters.</Text>
          </Flex>
        )}
      </Box>
      <Text fontSize="body.xs" color="gray.500">
        Starts from the {startYear} total and walks through every sub-category
        that changed, finishing on the {endYear} total.
        {showScope.value === "scope1" &&
          (drilledLabel
            ? ` Showing consumption-type changes within ${drilledLabel} — use the ✕ on the ${drilledLabel} pill to return to sub-categories.`
            : " Click a sub-category bar to drill into its consumption types.")}
        {showScope.value === "scope2" &&
          " Use the Basis toggle to switch between location-based and market-based calculations."}
      </Text>
    </Flex>
  );
};

export default WaterfallChart;
